// HindsightRouteGuard — route-level feature gate for /hindsight (defense-in-depth).
//
// Hiding the nav entries (separateMenu `featureGate: HINDSIGHT_ENABLED`) stops discovery, but a
// disabled user can still type or bookmark the /hindsight URL and land on the page, whose data
// calls then 404. This guard blocks the route before HindsightPage mounts and shows the same
// "unavailable" splash instead. Mirrors AidnRouteGuard.
//
// Gating: reuses the SAME source as the nav — isFeatureEnabled(HINDSIGHT_ENABLED) against the
// loaded feature list (account-resolved by the backend). While the list loads we show a spinner;
// when disabled we show the notice; when enabled we render the wrapped route. The backend 404 on
// /aidn/v1/hindsight/* remains the ultimate enforcement — this is UX, not the security boundary.

import PropTypes from 'prop-types';
import { Box, CircularProgress } from '@mui/material';
import { useCloudRestApiAccess } from 'contexts/CloudRestApiAccessContext';
import { isFeatureEnabled, isFeatureListLoaded, FEATURE_IDS } from 'utils/supportedFeatures';
import HindsightUnavailableNotice from './components/HindsightUnavailableNotice';

export default function HindsightRouteGuard({ children }) {
  const { loading: featureFlagsLoading } = useCloudRestApiAccess();
  const flagsReady = isFeatureListLoaded() || !featureFlagsLoading;

  if (!flagsReady) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }} data-testid="hindsight-route-guard-loading">
        <CircularProgress />
      </Box>
    );
  }

  if (!isFeatureEnabled(FEATURE_IDS.HINDSIGHT_ENABLED)) {
    return <HindsightUnavailableNotice />;
  }

  return children;
}

HindsightRouteGuard.propTypes = {
  children: PropTypes.node
};
